<script setup lang="ts">
type Offering = {
  icon: string
  title: string
  description: string
}

type Stat = {
  value: string
  label: string
  accent?: 'gold' | 'blue'
}

const props = defineProps<{
  kicker: string
  title: string
  heroDescription: string
  introHeading: string
  introParagraphs: string[]
  offeringsHeading: string
  offerings: Offering[]
  eventsHeading: string
  events: string[]
  stats?: Stat[]
  serviceHeading: string
  serviceDescription: string
  places: string[]
  ctaHeading: string
  ctaDescription: string
}>()

const stats = computed<Stat[]>(() => props.stats ?? [
  { value: '25+', label: 'Jahre Erfahrung', accent: 'gold' },
  { value: '500+', label: 'Events umgesetzt', accent: 'blue' },
  { value: '5★', label: 'Google Bewertung', accent: 'gold' },
])
</script>

<template>
  <div class="landing-page">
    <section class="landing-hero">
      <div class="landing-wrap landing-hero__inner">
        <p class="landing-kicker landing-kicker--gold">{{ kicker }}</p>
        <h1 class="landing-title">{{ title }}</h1>
        <p class="landing-hero__copy">{{ heroDescription }}</p>
        <div class="landing-actions">
          <Button as="router-link" to="/shop/request" class="px-8">Angebot anfordern</Button>
          <Button as="router-link" to="/contact" severity="secondary" class="px-8">Kontakt aufnehmen</Button>
        </div>
      </div>
    </section>

    <section class="landing-section landing-section--alt">
      <div class="landing-wrap">
        <div class="landing-split">
          <div class="landing-split__heading">
            <p class="landing-kicker">Direkt aus Flachau</p>
            <h2 class="landing-heading">{{ introHeading }}</h2>
          </div>
          <div class="landing-split__copy">
            <p
              v-for="(paragraph, index) in introParagraphs"
              :key="`${title}-intro-${index}`"
              class="landing-copy"
            >
              {{ paragraph }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section">
      <div class="landing-wrap">
        <div class="landing-split landing-split--stack">
          <div class="landing-split__heading">
            <p class="landing-kicker landing-kicker--gold">Leistungen</p>
            <h2 class="landing-heading">{{ offeringsHeading }}</h2>
          </div>
        </div>

        <div class="landing-grid landing-grid--three">
          <article
            v-for="offering in offerings"
            :key="`${title}-${offering.title}`"
            class="landing-card landing-card--feature"
          >
            <i :class="`${offering.icon} landing-card__icon`"></i>
            <h3 class="landing-card__title">{{ offering.title }}</h3>
            <p class="landing-card__copy">{{ offering.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="landing-section landing-section--alt">
      <div class="landing-wrap">
        <div class="landing-split landing-split--stack">
          <div class="landing-split__heading">
            <p class="landing-kicker landing-kicker--gold">Eventformate</p>
            <h2 class="landing-heading">{{ eventsHeading }}</h2>
          </div>
        </div>

        <div class="landing-chip-grid">
          <span
            v-for="event in events"
            :key="`${title}-${event}`"
            class="landing-chip"
          >
            {{ event }}
          </span>
        </div>
      </div>
    </section>

    <section class="landing-section">
      <div class="landing-wrap">
        <div class="landing-stats">
          <div
            v-for="stat in stats"
            :key="`${title}-${stat.label}`"
            class="landing-card landing-card--stat"
          >
            <div class="landing-stat" :class="stat.accent === 'blue' ? 'landing-stat--blue' : 'landing-stat--gold'">
              {{ stat.value }}
            </div>
            <div class="landing-stat__label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section landing-section--alt">
      <div class="landing-wrap">
        <div class="landing-split">
          <div class="landing-split__heading">
            <p class="landing-kicker">Region</p>
            <h2 class="landing-heading">{{ serviceHeading }}</h2>
          </div>
          <div class="landing-split__copy">
            <p class="landing-copy">{{ serviceDescription }}</p>
          </div>
        </div>

        <div class="landing-place-grid">
          <span
            v-for="place in places"
            :key="`${title}-${place}`"
            class="landing-place"
          >
            {{ place }}
          </span>
        </div>
      </div>
    </section>

    <section class="landing-section">
      <div class="landing-wrap">
        <div class="landing-cta">
          <p class="landing-kicker landing-kicker--gold">Anfrage</p>
          <h2 class="landing-heading landing-heading--center">{{ ctaHeading }}</h2>
          <p class="landing-copy landing-copy--center landing-cta__copy">{{ ctaDescription }}</p>
          <div class="landing-actions">
            <Button as="router-link" to="/shop/request" class="px-8">Angebot anfordern</Button>
            <Button as="a" href="tel:+436642247433" severity="secondary" class="px-8">+43 664 22 474 33</Button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.landing-page{
  background: #0b0b0c;
}

.landing-wrap{
  max-width: 1180px;
  margin: 0 auto;
}

.landing-hero,
.landing-section{
  padding: 4.5rem 1.5rem;

  @include desktop(){
    padding: 5.5rem 2rem;
  }
}

.landing-section--alt{
  background: #111214;
}

.landing-hero__inner,
.landing-cta{
  text-align: center;
}

.landing-kicker{
  margin: 0 0 0.75rem;
  color: rgba(243,242,238,0.72);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
  line-height: 16px;
  text-transform: uppercase;
}

.landing-kicker--gold{
  color: #E39737;
}

.landing-title,
.landing-heading{
  margin: 0;
  color: rgb(245 245 244);
  font-size: 38px;
  font-weight: 650;
  letter-spacing: -0.04em;
  line-height: 1.02;

  @include desktop(){
    font-size: 54px;
  }
}

.landing-heading{
  @include desktop(){
    font-size: 44px;
  }
}

.landing-heading--center{
  max-width: 14ch;
  margin-inline: auto;
}

.landing-hero__copy,
.landing-copy{
  color: rgb(214 211 209);
  font-size: 18px;
  line-height: 1.7;
}

.landing-hero__copy{
  max-width: 820px;
  margin: 1.25rem auto 0;

  @include desktop(){
    font-size: 20px;
  }
}

.landing-actions{
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: center;

  :deep(.p-button){
    border-radius: $public-radius-control;
  }

  @include tablet(){
    flex-direction: row;
  }
}

.landing-split{
  display: grid;
  gap: 1.5rem;
  align-items: end;

  @include desktop(){
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    gap: 3.5rem;
  }
}

.landing-split--stack{
  grid-template-columns: 1fr;
}

.landing-split__heading{
  max-width: 560px;
}

.landing-split__copy{
  display: grid;
  gap: 1rem;
}

.landing-copy{
  margin: 0;
}

.landing-grid{
  display: grid;
  gap: 1.25rem;
  margin-top: 2.75rem;
}

.landing-grid--three{
  @include tablet(){
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.landing-card{
  background: rgba(19,20,22,0.92);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: $public-radius-large;
}

.landing-card--feature{
  padding: 2rem;
  text-align: left;
}

.landing-card__icon{
  font-size: 2rem;
  margin-bottom: 1.25rem;
  color: #E39737;
}

.landing-card--feature:nth-child(2) .landing-card__icon{
  color: #4C9CD4;
}

.landing-card__title{
  margin: 0 0 0.75rem;
  color: rgb(245 245 244);
  font-size: 28px;
  font-weight: 620;
  letter-spacing: -0.03em;
  line-height: 1.12;
}

.landing-card__copy{
  margin: 0;
  color: rgb(214 211 209);
  font-size: 17px;
  line-height: 1.68;
}

.landing-chip-grid,
.landing-place-grid{
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2.75rem;
}

.landing-chip,
.landing-place{
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.05);
  background: rgba(19,20,22,0.92);
  color: rgb(231 229 228);
  padding: 0.8rem 1rem;
  font-size: 14px;
  line-height: 1.35;
}

.landing-stats{
  display: grid;
  gap: 1.25rem;

  @include tablet(){
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.landing-card--stat{
  padding: 2rem 1.5rem;
  text-align: center;
}

.landing-stat{
  font-size: 42px;
  font-weight: 760;
  letter-spacing: -0.05em;
  line-height: 1;
  margin-bottom: 0.75rem;
}

.landing-stat--gold{
  color: #E39737;
}

.landing-stat--blue{
  color: #4C9CD4;
}

.landing-stat__label{
  color: rgb(214 211 209);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
  line-height: 16px;
  text-transform: uppercase;
}

.landing-cta{
  max-width: 860px;
  margin: 0 auto;
  background: rgba(19,20,22,0.92);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 32px;
  box-shadow: 0 24px 52px rgba(0,0,0,0.24);
  padding: 2.5rem 1.5rem;

  @include desktop(){
    padding: 3.25rem;
  }
}

.landing-cta__copy{
  max-width: 42rem;
  margin: 1rem auto 0;
}

.landing-copy--center{
  text-align: center;
}
</style>
